import { useState, useRef, useEffect, useCallback } from "react";
import { Microphone, Stop, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { createOrchestratorTriggerAsync } from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

export default function FloatingMic() {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const holdTimer = useRef(null);

  const startRecording = useCallback(async () => {
    if (recording || processing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunks.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      recorder.onstop = () => { stream.getTracks().forEach(t => t.stop()); };
      recorder.start();
      mediaRecorder.current = recorder;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied");
    }
  }, [recording, processing]);

  const stopAndProcess = useCallback(async () => {
    if (!recording || !mediaRecorder.current) return;
    setRecording(false);
    
    // Stop recording
    const recorder = mediaRecorder.current;
    await new Promise(resolve => { recorder.onstop = resolve; recorder.stop(); });
    
    const blob = new Blob(chunks.current, { type: "audio/webm" });
    if (blob.size < 1000) { toast.error("Recording too short"); return; }
    
    setProcessing(true);
    try {
      // Transcribe
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/voice/transcribe`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      
      if (!res.ok) throw new Error("Transcription failed");
      const data = await res.json();
      const text = data.text?.trim();
      
      if (!text) { toast.error("Couldn't understand audio"); setProcessing(false); return; }
      
      setTranscript(text);
      
      // Queue the trigger (async — don't wait for orchestration)
      createOrchestratorTriggerAsync({
        trigger_type: "manual",
        source: "voice",
        context: { request: text, input_method: "voice" },
      }).then(() => {
        toast.success(`Voice: "${text.slice(0, 40)}${text.length > 40 ? '...' : ''}"`, { duration: 3000 });
      }).catch(() => {
        toast.error("Failed to queue trigger");
      });
      
      // Clear immediately — ready for next voice command
      setTimeout(() => setTranscript(""), 2000);
    } catch (e) {
      toast.error(e.message || "Voice failed");
    } finally {
      setProcessing(false);
    }
  }, [recording]);

  // Spacebar hold
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === "Space" && !e.repeat && !recording && !processing) {
        // Don't capture spacebar if user is typing in an input/textarea
        const tag = document.activeElement?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || document.activeElement?.contentEditable === "true") return;
        e.preventDefault();
        startRecording();
      }
    };
    const handleKeyUp = (e) => {
      if (e.code === "Space" && recording) {
        e.preventDefault();
        stopAndProcess();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => { window.removeEventListener("keydown", handleKeyDown); window.removeEventListener("keyup", handleKeyUp); };
  }, [recording, processing, startRecording, stopAndProcess]);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2" data-testid="floating-mic">
      {/* Transcript preview */}
      {(transcript || recording) && (
        <div className="bg-card border border-border rounded-lg shadow-xl px-4 py-2 max-w-[300px] animate-in slide-in-from-bottom-2">
          {recording && (
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-danger-solid animate-pulse" />
              <span className="text-sm text-foreground">Listening...</span>
            </div>
          )}
          {processing && (
            <div className="flex items-center gap-2">
              <CircleNotch className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Processing...</span>
            </div>
          )}
          {transcript && !processing && (
            <p className="text-xs text-muted-foreground italic">"{transcript}"</p>
          )}
        </div>
      )}

      {/* Mic button */}
      <button
        onMouseDown={startRecording}
        onMouseUp={stopAndProcess}
        onMouseLeave={() => { if (recording) stopAndProcess(); }}
        onTouchStart={startRecording}
        onTouchEnd={stopAndProcess}
        disabled={processing}
        className={`h-14 w-14 rounded-full shadow-xl flex items-center justify-center transition-all ${
          recording
            ? "bg-danger-solid scale-110 shadow-danger-solid/30"
            : processing
            ? "bg-muted cursor-wait"
            : "bg-primary hover:bg-primary/90 hover:scale-105"
        }`}
        data-testid="mic-button"
        title="Hold to speak (or hold Spacebar)"
      >
        {processing ? (
          <CircleNotch className="h-6 w-6 text-muted-foreground animate-spin" />
        ) : recording ? (
          <Stop className="h-6 w-6 text-white" weight="fill" />
        ) : (
          <Microphone className="h-6 w-6 text-primary-foreground" weight="fill" />
        )}
      </button>

      {/* Hint */}
      {!recording && !processing && (
        <span className="text-[9px] text-muted-foreground/40 mr-2">Hold Space</span>
      )}
    </div>
  );
}
